import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TableSkeleton from '../../../../components/common/TableSkeleton';
import CustomPagination from '../../../../components/common/CustomPagination';
import style from '../Advance.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppQuery } from '../../../../services/appQuery';
import { getcheckedInPatientList, Patient } from '../advance.helper';
import { getRequest } from '../../../../services/httpService';
import { PatientList } from '../../../../types';
import { convertDateToDDMMYYYY, convertUnderscoreToTitle, storePrevRoute } from '../../../../utils/commonUtils';
import { getRangeDates } from '../../../../components/Billing/Invoice/invoiceDateRanges.utils';
import { useClinic } from '../../../../components/ClinicProvider';
import { useTranslation } from 'react-i18next';

export interface CheckedInListProps {
  search: string;
  patientId?: number;
}

const CheckedInList: React.FC<CheckedInListProps> = ({ search, patientId }) => {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { ClinicId } = useClinic();  
  const location = useLocation();

  // Always use today's date range
  const defaultRange = getRangeDates('Today');
  const apiFromDate = defaultRange.fromDate;
  const apiToDate = defaultRange.toDate;
  const { t:tBilling } = useTranslation('billing_config');

  const [pageSize, setPageSize] = useState(10);
  
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);
  const queryKey = React.useMemo(() => [ 'checkedInPatients', page, pageSize, search, apiFromDate, apiToDate, ClinicId, patientId ], [page, pageSize, search, apiFromDate, apiToDate, ClinicId, patientId]);

  const { data: checkedInPatients, isLoading } = useAppQuery<Patient>(
    queryKey,
    () => getRequest<Patient>(
      `${getcheckedInPatientList()}&limit=${pageSize}&offset=${page * pageSize}` +
      (search ? `&search=${encodeURIComponent(search)}` : '') +
      `&to_date=${encodeURIComponent(apiToDate)}` +
      `&from_date=${encodeURIComponent(apiFromDate)}` +
      `&clinic=${ClinicId}` +
      (patientId ? `&patient_id=${encodeURIComponent(patientId)}` : '')
    ),
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: true,
      debounceMs: 300,
      refetchOnMount: true
    }
  );

  const onRowClick = (row: PatientList) => {
    // Placeholder for row click action
    if(!patientId){
      storePrevRoute(location.pathname, location.search);
    }
    navigate(`/admin/billing/advance/checked-in/${row.id}/${row.visit_id}`);

  };

  return (
    <Box data-testid="checked-in-list-container">
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none',  elevation: 0 }} data-testid="checked-in-table-container">
        <Table aria-label="Checked In list" data-testid="checked-in-table">
          <TableHead>
            <TableRow sx={{bgcolor:'#FAFAFA'}}>
              <TableCell className={style.tableHeader}>{tBilling('billing_config:Advance.date')}</TableCell>
              {!patientId && (
                <TableCell className={style.tableHeader}>{tBilling('billing_config:Advance.patient')}</TableCell>
              )}
              <TableCell className={style.tableHeader}>{tBilling('billing_config:Advance.department')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('billing_config:Advance.doctor')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('billing_config:Advance.reason')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableSkeleton
                columnWidths={patientId ? [120, 120, 140, 220] : [120, 200, 120, 140, 220]}
                rows={pageSize}
              />
            )}
            {!isLoading && checkedInPatients?.objects.map((row, idx) => (
              <TableRow key={idx} hover onClick={() => {onRowClick(row);}}>
                <TableCell className={style.tableCells} >{row?.checked_in_date ?convertDateToDDMMYYYY(row?.checked_in_date): '-'}</TableCell>
                {!patientId && (
                  <TableCell className={style.tableCells} >
                    <Stack spacing={0.25}>
                      <Typography variant="body2" className={style.tableCells} sx={{ fontWeight: 600 }}>
                        {row?.first_name + ' ' + row?.last_name} | {row?.age}
                      </Typography>
                      <Typography variant="caption" className={style.tableCells} color="text.secondary">
                        {row?.medical_record_no} | {row?.gender__name_label}
                      </Typography>
                    </Stack>
                  </TableCell>
                )}
                <TableCell className={style.tableCells} >
                  {Array.isArray(row?.appointment_user) && row.appointment_user.length > 0 &&
                   Array.isArray(row.appointment_user[0]?.departments) && row.appointment_user[0].departments.length > 0
                    ? row.appointment_user[0].departments[0]?.name ?? '-'
                    : '-'}
                </TableCell>
                <TableCell className={style.tableCells} >
                  {Array.isArray(row?.appointment_user) && row.appointment_user.length > 0
                    ? (() => {
                      const firstName = row.appointment_user[0]?.first_name ?? '';
                      const lastName = row.appointment_user[0]?.last_name ?? '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      // Add "Dr." prefix only if first_name doesn't already start with "Dr."
                      return fullName ? (firstName.startsWith('Dr.') ? fullName : `Dr. ${fullName}`) : '-';
                    })()
                    : '-'}
                </TableCell>
                <TableCell className={style.tableCells} >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" noWrap>{row?.reason_for_visit_label ?? convertUnderscoreToTitle(row?.reason_for_visit)}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && checkedInPatients?.objects.length === 0 && (
              <TableRow>
                <TableCell colSpan={patientId ? 4 : 5}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No entries found.
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPagination
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 15, 25, 50]}
        onPageSizeChange={handlePageSizeChange}
        totalCount={checkedInPatients?.meta?.total_count || 0}
        onPageChange={(_, newPage) => setPage(newPage)}
      />
    </Box>
  );
};

export default CheckedInList;
